// The full-screen dim (visuals §11, ui-qol §3): black over the arena and the HUD, eased linearly
// toward a target at a rate the app picks per transition:
//
//   boot / home before the first Play   0.5 instantly
//   first own cell drawn, spectate      → 0    at 1.5 /s  (0.5 → 0 in 0.33 s)
//   Esc while alive or spectating       → 0.5  at 1.25 /s (0 → 0.5 in 0.40 s)
//   own death                           → 0.5  at 0.16 /s (≈ 3.1 s)
//
// visuals §11 draws it as the canvas's last draw (renderer `setDim`, lane R3). Until the renderer
// has it, `createDimLayer` draws the same ramp as a DOM layer between the HUD and the menu; the
// app uses the renderer's when it exists and keeps this layer at 0.
//
// `DimRamp` is DOM-free (unit-tested).

import { h } from '../dom.js';

/** Dim targets and rates (visuals §11). */
export const DIM = {
  MENU: 0.5,
  /** 0.5 → 0 in 0.33 s. */
  UNDIM_RATE: 1.5,
  /** 0 → 0.5 in 0.40 s. */
  ESC_RATE: 1.25,
  /** 0 → 0.5 in ≈ 3.1 s. */
  DEATH_RATE: 0.16,
} as const;

/** A linear ramp: `set(target, ratePerSec, now)`, then `valueAt(now)`. */
export class DimRamp {
  private from: number;
  private target: number;
  private rate = 0;
  private startMs = 0;

  constructor(initial: number = DIM.MENU) {
    this.from = initial;
    this.target = initial;
  }

  /** Starts easing from the current value toward `target`; `rate` ≤ 0 or ∞ jumps there. */
  set(target: number, ratePerSec: number, nowMs: number): void {
    const t = Math.min(1, Math.max(0, target));
    this.from = this.valueAt(nowMs);
    this.target = t;
    this.rate = ratePerSec > 0 && Number.isFinite(ratePerSec) ? ratePerSec : 0;
    this.startMs = nowMs;
    if (this.rate === 0) this.from = t;
  }

  get targetValue(): number {
    return this.target;
  }

  valueAt(nowMs: number): number {
    if (this.rate === 0 || this.from === this.target) return this.target;
    const step = (this.rate * Math.max(0, nowMs - this.startMs)) / 1000;
    const dist = this.target - this.from;
    if (step >= Math.abs(dist)) return this.target;
    return this.from + Math.sign(dist) * step;
  }
}

export interface DimLayer {
  readonly el: HTMLElement;
  /** Draws `alpha` (DOM only on change, to 3 decimals). */
  render(alpha: number): void;
}

/**
 * With the canvas dim (renderer `setDim`), the DOM HUD sits above it; `brightness(1 − d)` on the
 * HUD layer gives the same picture as one dim over both (agar draws its HUD on the canvas, so its
 * leaderboard is dimmed too). Returns the filter value ('' = none).
 */
export function hudDimFilter(dim: number): string {
  const d = Math.round(Math.min(1, Math.max(0, dim)) * 1000) / 1000;
  return d > 0 ? `brightness(${1 - d})` : '';
}

export function createDimLayer(): DimLayer {
  const el = h('div', { class: 'dim-layer', 'aria-hidden': 'true' });
  let shown = -1;
  return {
    el,
    render(alpha: number): void {
      const a = Math.round(Math.min(1, Math.max(0, alpha)) * 1000) / 1000;
      if (a === shown) return;
      shown = a;
      el.style.opacity = String(a);
      el.hidden = a === 0;
    },
  };
}
